import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { UserPlus } from "lucide-react";

import { api } from "@/lib/api";
import { ROLE_LABELS } from "@/lib/constants";
import { fmtDate } from "@/lib/format";
import { useToast } from "@/components/toast";
import { Modal } from "@/components/modal";
import { AdminPage, Card, Gate, Meta } from "@/components/admin-ui";
import { Button, ChipGroup, ErrorState, Field, Loading, Pill, SectionLabel } from "@/components/ui";

const ROLES = Object.keys(ROLE_LABELS);
const toLabels = (r) => r.map((x) => ROLE_LABELS[x]);
const fromLabels = (l) => ROLES.filter((r) => l.includes(ROLE_LABELS[r]));
const EMPTY = { email: "", full_name: "", password: "", roles: [] };

export default function AdminStaff() {
  return <Gate perm="staff" testId="admin-staff-forbidden"><Inner /></Gate>;
}

function Inner() {
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const { data, isLoading, isError, error, refetch } = useQuery({ queryKey: ["admin-staff"], queryFn: () => api("/admin/staff") });
  const call = async (fn, msg, done) => {
    setBusy(true);
    try { await fn(); toast(msg, "success"); done(); refetch(); } catch (e) { toast(e.message, "error"); } finally { setBusy(false); }
  };
  return (
    <AdminPage title="Staff" testId="admin-staff-screen" right={<Button testId="admin-staff-create-button" title="Add" icon={UserPlus} size="sm" onClick={() => setCreating(true)} />}>
      <Meta>Document access is only for staff with the Verification reviewer role — grant it explicitly, even to super admins.</Meta>
      {isLoading ? <Loading /> : isError ? <ErrorState testId="admin-staff-error" message={error.message} onRetry={() => refetch()} /> : (
        <div className="grid gap-3 md:grid-cols-2">
          {data.staff.map((s) => (
            <Card key={s.id} testId={`admin-staff-row-${s.email}`} onClick={() => setEdit(s)}>
              <div className="flex justify-between gap-2"><span className="font-bold">{s.full_name}</span><Pill label={s.status} tone={s.status === "active" ? "success" : "error"} /></div>
              <Meta>{s.email} · last active {fmtDate(s.last_active_at)}</Meta>
              <div className="mt-2 flex flex-wrap gap-1.5">{s.roles.map((r) => <Pill key={r} label={ROLE_LABELS[r] ?? r} tone="rose" />)}</div>
            </Card>
          ))}
        </div>
      )}
      <Modal open={creating} onClose={() => setCreating(false)} title="New staff account" testId="admin-staff-create-sheet">
        <Field testId="admin-staff-email-input" label="Email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <Field testId="admin-staff-name-input" label="Full name" value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} />
        <Field testId="admin-staff-password-input" label="Temporary password" type="password" autoComplete="new-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} hint="8+ characters with letters and numbers" />
        <SectionLabel>Roles</SectionLabel>
        <ChipGroup testIdPrefix="admin-staff-role" options={toLabels(ROLES)} value={toLabels(f.roles)} onChange={(l) => setF({ ...f, roles: fromLabels(l) })} />
        <Button testId="admin-staff-create-submit" title="Create staff account" loading={busy} disabled={!f.roles.length} className="mt-6 w-full"
          onClick={() => call(() => api("/admin/staff", { method: "POST", body: f }), "Staff account created", () => { setCreating(false); setF(EMPTY); })} />
      </Modal>
      <Modal open={!!edit} onClose={() => setEdit(null)} title={edit?.full_name ?? ""} testId="admin-staff-edit-sheet">
        {edit ? (
          <>
            <Meta>{edit.email}</Meta>
            <div className="mt-4"><SectionLabel>Roles</SectionLabel></div>
            <ChipGroup testIdPrefix="admin-staff-edit-role" options={toLabels(ROLES)} value={toLabels(edit.roles)} onChange={(l) => setEdit({ ...edit, roles: fromLabels(l) })} />
            <Button testId="admin-staff-save-roles" title="Save roles" loading={busy} disabled={!edit.roles.length} className="mt-6 w-full"
              onClick={() => call(() => api(`/admin/staff/${edit.id}`, { method: "PATCH", body: { roles: edit.roles } }), "Roles updated", () => setEdit(null))} />
            <Button testId="admin-staff-toggle-active" title={edit.status === "active" ? "Deactivate account" : "Reactivate account"} variant={edit.status === "active" ? "danger" : "outline"} className="mt-2 w-full"
              onClick={() => call(() => api(`/admin/staff/${edit.id}`, { method: "PATCH", body: { active: edit.status !== "active" } }), "Account updated", () => setEdit(null))} />
          </>
        ) : null}
      </Modal>
    </AdminPage>
  );
}
